import React, { useState, useEffect } from 'react';
import { Play, X, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { getGalleryItems } from '../services/api';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jumpDiaries } from '../mockData';

const MediaShowcase = () => {
  const navigate = useNavigate();
  // Jump Diaries State
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [diaryVideos, setDiaryVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(true);

  // Gallery State
  const [galleryImages, setGalleryImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [galleryLoading, setGalleryLoading] = useState(true);

  // Helper function to extract YouTube video ID and get thumbnail
  const getYouTubeThumbnail = (url) => {
    if (!url) return null;
    
    let videoId = null;
    if (url.includes('youtube.com/watch?v=')) {
      videoId = url.split('v=')[1]?.split('&')[0];
    } else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('embed/')[1]?.split('?')[0];
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    }
    
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }
    return null;
  };

  // Helper to check if URL is a direct video/GIF file (not YouTube)
  const isDirectMedia = (url) => {
    if (!url) return false;
    // Check if it's NOT a YouTube URL
    const isYouTube = url.includes('youtube.com') || url.includes('youtu.be');
    if (isYouTube) return false;
    
    // Check if it has video/gif extensions or is a direct media URL
    const directMediaExtensions = ['.mp4', '.webm', '.mov', '.gif', '.gifv', '.avi', '.mkv'];
    const isDirect = directMediaExtensions.some(ext => url.toLowerCase().includes(ext));
    return isDirect;
  };

  // Fetch Jump Diaries
  useEffect(() => {
    const fetchDiaryVideos = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL || '';
        const response = await axios.get(`${API}/api/videos`);
        
        const diaries = response.data
          .filter(video => video.type === 'diary' && video.isActive)
          .map((video, index) => ({
            id: video._id || index,
            title: video.title,
            videoUrl: video.url,
            thumbnail: video.thumbnail || getYouTubeThumbnail(video.url),
            isDirectMedia: isDirectMedia(video.url)
          }));
        
        setDiaryVideos(diaries.length > 0 ? diaries : jumpDiaries.map(d => ({
          ...d,
          thumbnail: d.thumbnail || getYouTubeThumbnail(d.videoUrl),
          isDirectMedia: isDirectMedia(d.videoUrl)
        })));
      } catch (error) {
        console.error('Failed to fetch diary videos:', error);
        setDiaryVideos(jumpDiaries.map(d => ({
          ...d,
          thumbnail: d.thumbnail || getYouTubeThumbnail(d.videoUrl),
          isDirectMedia: isDirectMedia(d.videoUrl)
        })));
      } finally {
        setVideosLoading(false);
      }
    };

    fetchDiaryVideos();
  }, []);

  // Fetch Gallery
  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const data = await getGalleryItems({ type: 'image' });
        setGalleryImages(data);
      } catch (error) {
        console.error('Failed to fetch gallery:', error);
      } finally {
        setGalleryLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = () => {
    const newIndex = (currentIndex - 1 + galleryImages.length) % galleryImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  };

  const goToNext = () => {
    const newIndex = (currentIndex + 1) % galleryImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  };

  return (
    <section className="pt-0 pb-20 relative overflow-hidden" style={{ backgroundColor: '#FFEB3B' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* JUMP DIARIES SECTION - Left Text, Right Videos */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-20 pt-16">
          {/* Left Side - Text */}
          <div className="lg:col-span-4 flex flex-col justify-center">
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black leading-tight mb-6" style={{ color: '#00A8E1', fontFamily: 'Inter, sans-serif' }}>
              Jump<br />Diaries
            </h2>
            <p className="text-lg md:text-xl text-black mb-8 font-light">
              Watch our customers thrilling bungee jumping experiences captured on video.
            </p>
            <button
              onClick={() => navigate('/gallery')}
              className="inline-flex items-center space-x-2 text-black hover:text-blue-600 font-bold text-lg transition-colors"
             
            >
              <span>VIEW ALL</span>
              <ArrowRight size={24} />
            </button>
          </div>

          {/* Right Side - Video Grid */}
          <div className="lg:col-span-8">
            {videosLoading ? (
              <div className="text-center text-black">Loading videos...</div>
            ) : diaryVideos.length === 0 ? (
              <div className="text-center text-black">No videos available</div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {diaryVideos.slice(0, 4).map((diary, index) => (
                <div
                  key={diary.id}
                  className="group relative h-72 rounded-3xl overflow-hidden cursor-pointer transform hover:scale-105 transition-all duration-500 shadow-2xl"
                  onClick={() => !diary.isDirectMedia && setSelectedVideo(diary)}
                >
                  {/* Autoplay Video/GIF for direct media, otherwise show thumbnail */}
                  {diary.isDirectMedia ? (
                    // Autoplay video or GIF
                    <video 
                      className="absolute inset-0 w-full h-full object-cover"
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="auto"
                      crossOrigin="anonymous"
                    >
                      <source src={diary.videoUrl} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  ) : diary.thumbnail ? (
                    <img 
                      src={diary.thumbnail} 
                      alt={diary.title}
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="absolute inset-0 bg-black flex items-center justify-center">
                      <Play className="text-white" size={48} />
                    </div>
                  )}

                  {/* Dark Overlay - lighter for direct media */}
                  <div className={`absolute inset-0 ${diary.isDirectMedia ? 'bg-black/20 group-hover:bg-black/10' : 'bg-black/50 group-hover:bg-black/40'} transition-all duration-300`}></div>

                  {/* Play Button - Only show for YouTube videos */}
                  {!diary.isDirectMedia && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-white group-hover:bg-blue-500 flex items-center justify-center transform group-hover:scale-110 transition-all duration-300 shadow-lg">
                        <Play className="text-black group-hover:text-white ml-1" size={28} fill="currentColor" />
                      </div>
                    </div>
                  )}

                  {/* Title */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black via-black/80 to-transparent">
                    <h3 className="text-lg font-black text-white text-center">
                      {diary.title}
                    </h3>
                  </div>
                </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* OUR TREASURES SECTION - Left Text, Right Masonry Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Side - Text */}
          <div className="lg:col-span-4 flex flex-col justify-center">
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black leading-tight mb-6 text-black">
              Our<br />Treasures
            </h2>
            <p className="text-lg md:text-xl text-black mb-8 font-light">
              Explore our stunning collection of bungee jumping moments and memories.
            </p>
            <button
              onClick={() => navigate('/gallery')}
              className="inline-flex items-center space-x-2 text-black hover:text-blue-600 font-bold text-lg transition-colors"
             
            >
              <span>VIEW ALL</span>
              <ArrowRight size={24} />
            </button>
          </div>

          {/* Right Side - Masonry Grid */}
          <div className="lg:col-span-8">
            {galleryLoading ? (
              <div className="text-center text-black">Loading gallery...</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {galleryImages.slice(0, 6).map((image, index) => {
                  // Create varied heights for masonry effect
                  const heights = ['h-48', 'h-64', 'h-56', 'h-72', 'h-60', 'h-52'];
                  const heightClass = heights[index % heights.length];
                  
                  return (
                    <div
                      key={image.id}
                      className={`group relative ${heightClass} ${index === 0 || index === 3 ? 'md:col-span-2' : ''} rounded-3xl overflow-hidden cursor-pointer transform hover:scale-105 transition-all duration-500 shadow-2xl`}
                      onClick={() => openLightbox(image, index)}
                    >
                      {/* Image */}
                      <img
                        src={image.url}
                        alt={image.title}
                        className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                      />

                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>

                      {/* Title */}
                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 className="text-white font-black text-base md:text-lg">
                          {image.title}
                        </h3>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-5xl">
            <button
              onClick={() => setSelectedVideo(null)}
              className="absolute -top-12 right-0 text-white hover:text-yellow-400 transition-colors"
              aria-label="Close video"
            >
              <X size={32} />
            </button>

            <div className="aspect-w-16 aspect-h-9 rounded-2xl overflow-hidden shadow-2xl">
              {selectedVideo.videoUrl.includes('youtube.com') || selectedVideo.videoUrl.includes('youtu.be') ? (
                <iframe
                  className="w-full h-96 md:h-[500px] rounded-2xl"
                  src={selectedVideo.videoUrl}
                  title={selectedVideo.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              ) : (
                <video
                  className="w-full h-96 md:h-[500px] rounded-2xl bg-black"
                  controls
                  autoPlay
                  src={selectedVideo.videoUrl}
                >
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm p-4">
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white hover:text-yellow-400 transition-colors z-10"
            aria-label="Close"
          >
            <X size={32} />
          </button>

          <button
            onClick={goToPrevious}
            className="absolute left-6 top-1/2 -translate-y-1/2 bg-yellow-400 hover:bg-yellow-300 text-black p-3 rounded-full shadow-lg transform hover:scale-110 transition-all duration-300"
            aria-label="Previous"
          >
            <ChevronLeft size={24} />
          </button>

          <button
            onClick={goToNext}
            className="absolute right-6 top-1/2 -translate-y-1/2 bg-yellow-400 hover:bg-yellow-300 text-black p-3 rounded-full shadow-lg transform hover:scale-110 transition-all duration-300"
            aria-label="Next"
          >
            <ChevronRight size={24} />
          </button>

          <div className="max-w-5xl w-full">
            <img
              src={selectedImage.url}
              alt={selectedImage.title}
              className="w-full h-auto rounded-2xl shadow-2xl"
            />
            <p className="text-white text-center mt-4 text-xl font-bold">{selectedImage.title}</p>
          </div>
        </div>
      )}
    </section>
  );
};

export default MediaShowcase;
